import { useAppSelector } from '@store/hooks';
import {
  selectActiveBankAccount,
  selectActiveCompany,
  selectBankAccounts,
  selectLatestProductsStatusInfo,
} from '@features/workspace/store/selectors';
import { CompanyTypeLabelEnum, getAddressText } from '@utils/constants';
import Select from '@components/ui/Select';
import { useNavigate } from 'react-router-dom';

const InfoBlock = ({ label, value }: { label: string; value: string }) => (
  <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
    <div className="self-stretch text-navy-700 text-subtitle2a">{label}</div>
    <div className="self-stretch text-navy-600 text-body3">{value}</div>
  </div>
);

const BankingHeader = () => {
  const company = useAppSelector(selectActiveCompany);
  const latestProductsInfo = useAppSelector(selectLatestProductsStatusInfo);
  const bankAccounts = useAppSelector(selectBankAccounts);
  const activeBankAccount = useAppSelector(selectActiveBankAccount);
  const navigate = useNavigate();
  const options = bankAccounts.map((account) => ({
    label: `${account.name} - Acc No. ${account.accountReference}`,
    value: account.accountNumber,
  }));

  const onActiveBankAccountChange = (accNumber: string | number) => {
    navigate(`/customer/banking/${accNumber}/transactions`);
  };

  const accountDetails = [
    {
      label: 'My business account',
      value: `Acc No. ${activeBankAccount?.accountReference || '-'}`,
    },
    {
      label: 'Available Balance:',
      value:
        activeBankAccount?.currency !== 'ZAR'
          ? `${activeBankAccount?.amount || '-'} ${
              activeBankAccount?.currency || ''
            }`
          : `R ${activeBankAccount.amount || '-'}`,
    },
    {
      label: 'Start of Banking Application:',
      value: latestProductsInfo[0]?.startDate || '-',
    },
    {
      label: 'Start of Funding Application:',
      value: latestProductsInfo[1]?.startDate || '-',
    },
  ];

  return (
    <div className="h-[148px] bg-white rounded-lg">
      <div className="px-6 py-4 flex items-center justify-between">
        <div>
          <h6 className="text-navy-700 text-h6">
            {company?.name}
            <span className="text-navy-400 ml-1">
              ({CompanyTypeLabelEnum[company?.companyType as number]})
            </span>
          </h6>
          <div className="text-navy-600 text-body3 mt-1">
            {getAddressText(company?.address)}
          </div>
        </div>
        <div>
          <Select
            disabled={!bankAccounts || bankAccounts.length < 2}
            fixedText="Switch accounts"
            inputClassName="disabled:bg-white"
            options={options}
            value={activeBankAccount?.accountNumber}
            onChange={onActiveBankAccountChange}
          />
        </div>
      </div>
      <div className="w-full border-grey-200 border-b flex justify-between items-center" />
      <div className="px-6 py-4 flex justify-between items-center gap-4">
        {accountDetails.map(({ label, value }, index) => (
          <InfoBlock key={index} label={label} value={value} />
        ))}
      </div>
    </div>
  );
};

export default BankingHeader;
